import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { usePolling } from '../hooks/useApi';
import { truncateDecimalAmount } from '../utils/numberFormat';
import './ManualTradeRecovery.css';

type Quote = {
  quoteId: string; tradeId: number; expiresAt: number; tokenIn: string; tokenOut: string; amountIn: string;
  amountOut: string; minAmountOut: string; returnedAmount: string; originalAmount: string;
  totalExpected: string; totalMinimum: string; expectedLossPct: number; worstLossPct: number;
  maxLossPct: number; slippage: number; requiresLossOverride: boolean; provider: string; attachedTon: string;
};
const fmt = (n: string | number) => truncateDecimalAmount(n, 2);

export function AutoTradeRecovery() {
  const { data, error: loadError, refetch } = usePolling(() => api.getAutoRecovery(), 2000);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = async () => {
    if (!data || saving || loadError) return;
    setSaving(true); setError(null);
    try { await api.setAutoRecovery(!data.enabled); await refetch(); }
    catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };
  const status = ({ disabled: 'Выключено', idle: 'Ожидает', checking: 'Проверяет', blocked: 'Заблокировано',
    confirming: 'Возврат проверяется', attention: 'Нужна сверка' } as Record<string, string>)[data?.status];
  return <section className="card manual-recovery" aria-label="Автовосстановление позиций">
    <div className="manual-recovery-heading">
      <h3>Автовосстановление</h3>
      <div className="manual-recovery-actions">
        <span className="text-xs" role="status">{saving ? 'Сохраняю...' : status || 'Загрузка...'}</span>
        <button type="button" role="switch" aria-label="Автовосстановление" aria-checked={!!data?.enabled}
          className={'toggle recovery-toggle' + (data?.enabled ? ' active' : '')}
          disabled={!data || !!loadError || saving} onClick={toggle} />
      </div>
    </div>
    <p className="text-xs text-muted">Возврат незавершённой DEX-сделки в исходный токен без ручного подтверждения. CEX не запускается.</p>
    <p className="text-xs text-muted">Slippage и лимит потерь — из сделки. Газ отдельно. Выключение не отменяет уже отправленный обмен.</p>
    {data?.enabled && data.reason && <p className="text-xs" role="status">
      {data.tradeId ? `#${data.tradeId}: ` : ''}{data.reason}
    </p>}
    {data?.enabled && data.status === 'blocked' && data.nextCheckAt && <p className="text-xs text-muted">
      Повторная проверка через {Math.max(0, Math.ceil((data.nextCheckAt - Date.now()) / 1000))} с
    </p>}
    {(error || loadError) && <div className="text-danger" role="alert">{error || loadError}</div>}
  </section>;
}

export function ManualTradeRecovery({ canExecute, onChange }: { canExecute: boolean; onChange: () => unknown }) {
  const { data, error: loadError, refetch } = usePolling(() => api.getManualRecovery(), 2000);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => { setQuote(null); setAccepted(false); }, [data?.tradeId, data?.status]);
  const expired = !!quote && now >= quote.expiresAt;

  const prepare = async () => {
    setPreparing(true); setError(null); setQuote(null); setAccepted(false);
    try { setQuote(await api.prepareManualRecovery(data.tradeId)); setNow(Date.now()); }
    catch (e) { setError((e as Error).message); }
    finally { setPreparing(false); }
  };
  const execute = async () => {
    if (!quote || sending || Date.now() >= quote.expiresAt || !canExecute || (quote.requiresLossOverride && !accepted)) return;
    setSending(true); setError(null);
    try {
      await api.executeManualRecovery(quote.tradeId, { quoteId: quote.quoteId, confirmation: 'EXECUTE_TRADE_RECOVERY', acceptLossAboveLimit: accepted });
    } catch (e) { setError((e as Error).message); }
    finally {
      setQuote(null); setAccepted(false); setSending(false);
      await refetch(); onChange();
    }
  };
  if (loadError) return <div className="card text-danger" role="alert">Не удалось загрузить восстановление: {String(loadError)}</div>;
  if (!data) return null;
  const plan = quote || data.plan;
  const status = ({ blocked: 'Нужно восстановление', submitting: 'Отправляется', confirming: 'Проверяется в TON',
    completed: 'Восстановлено', attention: 'Нужна сверка' } as Record<string, string>)[data.status] || data.status;
  return <section className="card manual-recovery" aria-label="Ручное восстановление сделки">
    <div className="manual-recovery-heading">
      <h3>{data.attempt?.mode === 'automatic' ? 'Автовосстановление' : 'Восстановление'} {data.tradeId ? `#${data.tradeId}` : 'сделки'}</h3>
      <span className={'badge ' + (data.status === 'completed' ? 'badge-success' : 'badge-warning')}>{status || 'Заблокировано'}</span>
    </div>
    {data.reason && !quote && <p className="text-xs text-muted" role="status">{data.reason}</p>}
    {plan && <div className="manual-recovery-facts">
      <span>Обмен: <strong title={plan.amountIn}>{fmt(plan.amountIn)} {plan.tokenIn} → {plan.tokenOut}</strong></span>
      <span>Уже вернулось: <strong title={plan.returnedAmount}>{fmt(plan.returnedAmount)} {plan.tokenOut}</strong></span>
      <span>Исходная сумма: <strong>{fmt(plan.originalAmount)} {plan.tokenOut}</strong></span>
    </div>}
    {data.canPrepare && <button type="button" className="btn btn-outline" disabled={preparing || sending} onClick={prepare}>
      {preparing ? 'Проверяю trace и котировку...' : `Подготовить восстановление #${data.tradeId}`}
    </button>}
    {quote && <>
      <dl className="manual-recovery-quote">
        <div><dt>Роутер</dt><dd>{quote.provider} · прямой обмен</dd></div>
        <div><dt>Получение</dt><dd title={quote.amountOut}>{fmt(quote.amountOut)} {quote.tokenOut}</dd></div>
        <div><dt>Минимум получения</dt><dd title={quote.minAmountOut}>{fmt(quote.minAmountOut)} {quote.tokenOut}</dd></div>
        <div><dt>Всего после возврата</dt><dd>{fmt(quote.totalExpected)} {quote.tokenOut}</dd></div>
        <div><dt>Минимум всего</dt><dd>{fmt(quote.totalMinimum)} {quote.tokenOut}</dd></div>
        <div><dt>Ожидаемые потери</dt><dd>{fmt(quote.expectedLossPct)}%</dd></div>
        <div><dt>Потери при minOut</dt><dd title={String(quote.worstLossPct)}>{fmt(quote.worstLossPct)}% без газа</dd></div>
        <div><dt>Прикреплено TON, не комиссия</dt><dd>{fmt(quote.attachedTon)} TON</dd></div>
      </dl>
      {quote.requiresLossOverride && <label className="manual-recovery-accept">
        <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} disabled={sending || expired} />
        <span>Разрешаю превышение лимита {fmt(quote.maxLossPct)}% только для этой попытки. При minOut потери {fmt(quote.worstLossPct)}% плюс газ. Общий лимит не меняется.</span>
      </label>}
      <div className="manual-recovery-actions">
        <button type="button" className="btn btn-primary" onClick={execute}
          disabled={!canExecute || sending || preparing || expired || (quote.requiresLossOverride && !accepted)}>
          {sending ? 'Отправляется...' : 'Подтвердить восстановление'}
        </button>
        <span className="text-xs text-muted">{expired ? 'Котировка устарела, запросите новую' : `Котировка: ${Math.max(0, Math.ceil((quote.expiresAt - now) / 1000))} с`}</span>
      </div>
      <p className="text-xs text-muted">Остальные сделки заблокированы до подтверждения возврата. Газ не входит в расчёт потерь.</p>
    </>}
    {data.attempt?.signedMessageHash && <div className="text-xs manual-recovery-hash">Hash: {data.attempt.signedMessageHash}</div>}
    {error && <div className="text-danger" role="alert">{error}</div>}
  </section>;
}
